<script>
import axios from 'axios'
import LineChart from '@components/chart.vue'
import { getRelativePosition } from 'chart.js/helpers';
// import moment from 'moment'
import { mixin as clickaway } from 'vue-clickaway';
import { authComputed } from '@state/helpers'
import CameraTemperatureSnapshotModal from '@components/camera-temperature-snapshot-modal.vue'


export default {
  components: { LineChart, CameraTemperatureSnapshotModal },
  mixins: [ clickaway ],
  props: {
    camera: {
      type: Object,
      required: true,
    },
    selectedPreset: {
      type: [Number],
      required: false,
    }
  },
  data() {
    return {
      showModal: false,
      modalHeader: '',
      modalImgUrl: '',
      loaded: false,
      timestamps: null,
      presets: [],
      presetDropdown: 'closed',
      chartPolling: null,
      tip: 'closed',
      timeDropdown: 'closed',
      timeRange: [],
      selectedTime: 30,
      selectedLabel: '30 minutes',
      timeOptions: [
        { time: 30, label: '30 minutes' },
        { time: 60, label: '1 hour' },
        { time: 120, label: '2 hours' },
      ],
    }
  },
  computed: {
    ...authComputed
  },
  watch: {
    selectedPreset: function () {
      this.requestChartData();
    }
  },
  mounted() {
    this.requestChartData()
    this.chartPolling = setInterval(this.requestChartData, 300000)
  },

  beforeDestroy() {
    clearInterval(this.chartPolling)
  },
  methods: {
    closeModal: function() {
      this.showModal = false;
      this.modalHeader= '';
      this.modalImgUrl= '';
    },
    openDropdown: function() {
      if (this.timeDropdown === 'closed') {
        clearInterval(this.chartPolling)
        this.timeDropdown = 'open'
      } else {
        this.setSelectedTime(this.selectedTime, this.selectedLabel)
      }
    },
    closeDropdown: function() {
      if (this.timeDropdown === 'open') {
        this.timeDropdown = 'close'
        this.setSelectedTime(this.selectedTime, this.selectedLabel)
      }
    },
    setSelectedTime: function(time, label) {
      this.selectedTime = time
      this.selectedLabel = label
      this.timeDropdown = 'closed'

      this.requestChartData()
      clearInterval(this.chartPolling)
      this.chartPolling = setInterval(this.requestChartData, 30000)
    },
    requestChartData: function() {
      if (this.camera.uuid && this.selectedPreset) {
        this.loaded = false;
        axios
          .get(`/data/api/temperature/${this.camera.uuid}?minutes=${this.selectedTime}&preset=${this.selectedPreset}`,
              { headers: { Authorization: `Bearer ${this.currentUser.jwt}` } })
          .then((response) => {
              const data = [];
              this.timestamps = [];

              for (const temperature of response.data) {
                data.push(Math.max(...temperature.temperatures));
                this.timestamps.push(temperature.timestamp);
                this.timestampsInUnix = this.timestamps.map((timestamp) => {
                  return new Date(`${timestamp}+00:00`).getTime();
                });
              }

              this.chartoptions = {
                onClick: (pointerEvent, chartElementArray) => {
                  const chart = this.$refs.chart.$refs.chart.getCurrentChart();

                  const canvasPosition = getRelativePosition(pointerEvent, chart);

                  // Substitute the appropriate scale IDs
                  const dataX = chart.scales.x.getValueForPixel(canvasPosition.x);

                  const closestTimestamp = this.timestampsInUnix.reduce((a, b) => {
                    const aDiff = Math.abs(a - dataX);
                    const bDiff = Math.abs(b - dataX);

                    if (aDiff === bDiff) {
                      return a > b ? a : b;
                    } else {
                      return bDiff < aDiff ? b : a;
                    }
                  });

                  const closestTimestampIndex = this.timestampsInUnix.indexOf(closestTimestamp);

                  axios.get(
                    `/data/api/panorama/${this.camera.uuid}/${encodeURIComponent(this.timestamps[closestTimestampIndex])}`,
                    { headers: { Authorization: `Bearer ${this.currentUser.jwt}` } }
                  ).then((response) => {
                    this.showModal = true;
                    this.modalImgUrl = response.data.image;
                    this.modalHeader = this.chartdata.labels[closestTimestampIndex];
                  });
                },

                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true
                  },
                  x: {
                    type: 'time',
                    time: {
                      unit: 'minute'
                    },
                    min: this.timestampsInUnix[0],
                    max: this.timestampsInUnix[this.timestampsInUnix - 1],
                  }
                }
              };

              this.chartdata = {
                labels: this.timestamps.map((t) => new Date(`${t}+00:00`).toString()),
                datasets: [
                  {
                    label: "Maximum",
                    borderColor: 'red',
                    data: data
                  }
                ]
              };

              this.loaded = true;
          })
      }
    }
  },
}
</script>

<template>
  <div :class="$style.cameraTemperature">
    <header :class="$style.header">
      <BaseCircle size="large"
        ><svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M15.499 6.914l-9.263 9.265a2.357 2.357 0 00-.798-.147 2.358 2.358 0 101.668 4.025 2.353 2.353 0 00.544-2.464l9.263-9.265a.999.999 0 10-1.414-1.414" fill="currentColor"/><path d="M21.2 5.666L9.173 17.693a.996.996 0 00-.293.705 3.42 3.42 0 01-1.008 2.426 3.417 3.417 0 01-2.434 1.008c-.92 0-1.783-.357-2.433-1.007a3.448 3.448 0 010-4.869 3.426 3.426 0 012.425-1.007.999.999 0 00.704-.293l12.03-12.028A2.127 2.127 0 0119.68 2a2.149 2.149 0 011.52 3.666m1.414-4.452A4.127 4.127 0 0019.68 0a4.119 4.119 0 00-2.931 1.215L4.995 12.967a5.405 5.405 0 00-3.404 1.575 5.45 5.45 0 000 7.697 5.402 5.402 0 003.847 1.593 5.395 5.395 0 003.848-1.595 5.382 5.382 0 001.576-3.405L22.614 7.08a4.153 4.153 0 000-5.866" fill="currentColor"/></svg></BaseCircle>
      <h2>Temperature</h2>
    </header>
    <div :class="$style.content">
      <div :class="$style.toolbar">
        <h3>{{ timeRange.join(' - ') }} <span>(maximum temperature)</span></h3>
        <div :class="$style.options">
          <div v-on-clickaway="closeDropdown" :class="$style.dropdown">
            <div :class="$style.dropdownClosed" @click="openDropdown">
              <svg width="24" height="24" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" :class="$style.iconClock" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M8.001 0C12.411 0 16 3.588 16 7.999 16 12.412 12.412 16 8.001 16S0 12.412 0 8l.004-.249C.136 3.454 3.674 0 8 0zm.629 1.404a.686.686 0 01-1.256 0 6.57 6.57 0 00-2.382.697l.187.326a.343.343 0 01-.594.342l-.187-.325a6.674 6.674 0 00-1.953 1.952l.325.187a.343.343 0 11-.34.595l-.327-.188a6.558 6.558 0 00-.7 2.382.684.684 0 010 1.255 6.56 6.56 0 00.699 2.382l.328-.187a.343.343 0 11.34.596l-.326.185a6.67 6.67 0 001.954 1.953l.186-.324a.342.342 0 11.595.341l-.187.325c.727.373 1.53.618 2.382.699a.684.684 0 011.256 0 6.576 6.576 0 002.382-.7l-.188-.322a.343.343 0 01.594-.345l.188.325a6.673 6.673 0 001.95-1.953l-.322-.185a.343.343 0 11.342-.595l.324.187a6.57 6.57 0 00.697-2.382.685.685 0 010-1.255 6.571 6.571 0 00-.698-2.381l-.323.186a.342.342 0 11-.343-.594l.323-.186a6.666 6.666 0 00-1.952-1.953l-.187.325a.343.343 0 01-.595-.342l.187-.326a6.564 6.564 0 00-2.38-.697zm-.628.962c.379 0 .685.307.685.686V8c0 .211-.1.394-.25.52-.013.014-.019.032-.032.046l-1.99 1.99a.683.683 0 01-.97 0 .686.686 0 010-.97l1.87-1.87V3.052c0-.379.307-.686.687-.686z" fill="currentColor"/></svg>
              <span>{{ selectedLabel }}</span>
              <svg width="24" height="24" viewBox="0 0 13 8" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" :class="$style.iconArrow" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M.801 2.247A1.019 1.019 0 01.801.8a1.03 1.03 0 011.453 0L6.5 5.03 10.746.8a1.03 1.03 0 011.754.723c0 .262-.1.524-.301.724L7.226 7.2a1.033 1.033 0 01-1.452 0L.8 2.247z" fill="currentColor"/></svg>
            </div>
            <div v-if="timeDropdown === 'open'" :class="$style.dropdownOpen">
              <ul>
                <li
                  v-for="option in timeOptions"
                  :key="option.time"
                  @click="setSelectedTime(option.time, option.label)"
                  >{{ option.label }}</li
                >
              </ul>
            </div>
          </div>


        </div>
      </div>
      <LineChart
        v-if="loaded && selectedPreset !== null"
        ref="chart"
        :chart-data="chartdata"
        :options="chartoptions"
        :class="$style.chartContainer"
      ></LineChart>
      <span v-if="selectedPreset === null">Select a preset in the panorama view to see its temperatures</span>
      <div v-if="loaded && selectedPreset !== null" :class="$style.legend">
        <div :class="$style.legendItem">
          <span :class="$style.legendIcon"></span>
          <span :class="$style.legendText">Maximum temp.</span>
        </div>
      </div>
    </div>
    <footer v-if="loaded && selectedPreset !== null" :class="$style.footer">
      <div :class="$style.tip">
        <div :class="$style.intro">
          <div :class="$style.introLeft">
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g fill="currentColor" fill-rule="evenodd"><path d="M11.645 15.231c-.318 0-.586.106-.805.32a1.06 1.06 0 00-.328.788c0 .353.113.627.34.823.225.196.49.294.793.294.291 0 .551-.1.773-.298.223-.198.334-.471.334-.819a1.07 1.07 0 00-.318-.788 1.073 1.073 0 00-.789-.32M13.727 6.588c-.549-.266-1.186-.398-1.904-.398-.774 0-1.451.159-2.032.476-.58.318-1.021.718-1.324 1.202-.302.484-.455.96-.455 1.432 0 .228.096.44.287.637a.95.95 0 00.703.294c.471 0 .791-.281.961-.841.178-.536.397-.942.655-1.217.257-.275.66-.413 1.205-.413.467 0 .847.137 1.142.409.295.273.444.608.444 1.005 0 .204-.049.392-.147.565a2.101 2.101 0 01-.357.473 14.76 14.76 0 01-.688.629c-.361.317-.65.59-.863.822s-.385.499-.514.804-.193.666-.193 1.082c0 .333.088.583.264.752a.904.904 0 00.652.253c.496 0 .791-.258.885-.774.054-.243.095-.413.123-.51.027-.096.066-.193.115-.29s.125-.203.227-.32c.101-.116.238-.252.406-.405a46.75 46.75 0 001.269-1.165c.235-.231.438-.504.61-.822s.256-.687.256-1.11c0-.535-.149-1.031-.45-1.487-.3-.457-.726-.818-1.277-1.083"/><path d="M18.692 18.688c-1.848 1.847-4.305 2.864-6.916 2.864S6.71 20.536 4.864 18.69s-2.863-4.302-2.863-6.914S3.018 6.71 4.866 4.862C6.712 3.016 9.167 2 11.778 2s5.066 1.016 6.912 2.862c3.813 3.812 3.813 10.014.002 13.826m1.412-15.24A11.697 11.697 0 0011.778 0a11.69 11.69 0 00-8.326 3.448 11.697 11.697 0 00-3.451 8.328c0 3.146 1.224 6.104 3.449 8.328a11.69 11.69 0 008.326 3.448c3.146 0 6.104-1.225 8.33-3.45 4.59-4.59 4.59-12.062-.002-16.654"/></g></svg>
          </div>
          <div :class="$style.introRight">
            <h2>How to interpret the graph</h2>
            <h3>The graph is showing the maximum temperature per minute.</h3>
          </div>
        </div>
        <div v-if="tip === 'open'" :class="$style.extend">
          <p
            >A short peak in the graph data could, for example, mean that there
            was an exhaust or hydraulic lines in view. The data in the graph is
            refreshed automatically. You can choose between the following time
            ranges, 30 minutes, 1 hour and 2 hours.</p
          >
        </div>
        <div
          v-if="tip === 'closed'"
          :class="$style.tipArrow"
          @click="tip = 'open'"
          ><svg width="24" height="24" viewBox="0 0 13 8" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M.801 2.247A1.019 1.019 0 01.801.8a1.03 1.03 0 011.453 0L6.5 5.03 10.746.8a1.03 1.03 0 011.754.723c0 .262-.1.524-.301.724L7.226 7.2a1.033 1.033 0 01-1.452 0L.8 2.247z" fill="currentColor"/></svg></div>
        <div
                v-if="tip === 'open'"
                :class="$style.tipArrowClosed"
                @click="tip = 'closed'"
        ><svg width="24" height="24" viewBox="0 0 13 8" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M.801 2.247A1.019 1.019 0 01.801.8a1.03 1.03 0 011.453 0L6.5 5.03 10.746.8a1.03 1.03 0 011.754.723c0 .262-.1.524-.301.724L7.226 7.2a1.033 1.033 0 01-1.452 0L.8 2.247z" fill="currentColor"/></svg></div>
      </div>
    </footer>
      <!-- use the modal component, pass in the prop -->
      <CameraTemperatureSnapshotModal :header="modalHeader" :img-url="modalImgUrl" :show="showModal" @close="closeModal">
      </CameraTemperatureSnapshotModal>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.cameraTemperature {
  padding: 33px 18px;
  margin-bottom: 16px;
  background: $color-white;

  @media (min-width: $breakpoint-md) {
    padding: 78px;
  }

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 36px;

    h2 {
      margin: 0 0 0 13px;
      font-size: 18px;
      font-weight: 500;
      letter-spacing: -0.79px;

      @media (min-width: $breakpoint-md) {
        margin: 0 0 0 24px;
        font-size: 24px;
      }
    }
  }

  .content {
    @media (min-width: $breakpoint-md) {
      padding: 0 78px;
    }

    .chartContainer {
      position: relative;
      width: 100%;
      height: 250px;
      overflow: hidden;

      canvas {
        width: 100% !important;
      }
    }

    .toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;

      h3 {
        font-size: 18px;
        font-weight: normal;
        color: transparentize(#305c6e, 0.29);
        letter-spacing: -0.46px;

        span {
          display: none;

          @media (min-width: $breakpoint-md) {
            display: inline-block;
          }
        }
      }

      .options {
        .dropdown {
          position: relative;

          .dropdownClosed {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 150px;
            height: 39px;
            padding: 0 15px;
            color: $color-white;
            cursor: pointer;
            background: $color-button-bg;
            background-image: $color-button-bg-gradient;
            border-radius: 24px;
            transition: background 0.5s ease;

            span {
              display: inline-block;
              margin: 0 9px;
            }

            .iconClock {
              width: 16px;
              height: 16px;
            }

            .iconArrow {
              width: 12px;
              height: 12px;
            }

            &:hover {
              background: $color-button-bg;
              background-image: none;
            }
          }

          .dropdownOpen {
            position: absolute;
            top: 42px;
            right: 0;
            left: 0;
            z-index: 9999;
            background: $color-white;
            border: 1px solid $color-button-bg;
            border-radius: 15px;
            box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);

            ul {
              padding: 3px 0;
              margin: 0;
              list-style: none;

              li {
                display: block;
                padding: 8px 20px;
                cursor: pointer;
                border-bottom: 1px solid transparentize($color-gray-alt, 0.79);

                &:last-child {
                  border-bottom: 0;
                }

                &:hover {
                  color: $color-primary;
                }
              }
            }
          }
        }
      }
    }

    .legend {
      margin-top: 30px;

      .legendItem {
        display: flex;
        align-items: center;

        .legendIcon {
          display: inline-block;
          width: 46px;
          height: 2px;
          margin-right: 10px;
          border: solid 2px $color-graph;
        }

        .legendText {
          font-size: 13px;
          color: transparentize($color-text, 0.45);
          letter-spacing: -0.09px;
        }
      }
    }
  }

  .footer {
    margin-top: 25px;

    @media (min-width: $breakpoint-md) {
      padding: 0 78px;
    }

    .tip {
      position: relative;
      padding: 24px 32px;
      border: 2px solid transparentize($color-gray-alt, 0.79);
      border-radius: 5px 5px 25px 5px;

      .intro {
        display: flex;

        .introLeft {
          margin-right: 12px;
          color: $color-text;
        }

        .introRight {
          h2,
          h3 {
            margin: 0;
            font-weight: normal;
          }

          h2 {
            font-size: 18px;
            line-height: 24px;
            letter-spacing: -0.59px;
          }

          h3 {
            font-size: 15px;
            color: transparentize($color-text, 0.31);
            letter-spacing: -0.1px;
          }
        }
      }

      .extend {
        padding-left: 36px;
        color: transparentize($color-text, 0.31);
      }

      .tipArrow {
        position: absolute;
        right: 0;
        bottom: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 45px;
        height: 39px;
        color: $color-primary;
        cursor: pointer;
        border-top: 2px solid transparentize($color-gray-alt, 0.79);
        border-left: 2px solid transparentize($color-gray-alt, 0.79);
        border-radius: 5px 0 25px 0;
        transition: background-color 0.5s ease;


        svg {
          width: 12px;
          height: 10px;
        }

        &:hover {
          background: transparentize($color-gray-alt, 0.79);
        }
      }
      .tipArrowClosed{
        @extend .tipArrow;
        svg {
          width: 12px;
          height: 10px;
          transform: rotate(180deg);
        }
      }
    }
  }
}
</style>
